import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesAPI, voipAPI, contactsAPI, segmentsAPI, productLinesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Building, Globe, MapPin, Users, Edit, Trash2, Phone, Star, User, Eye } from 'lucide-react';

export const CompaniesPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [segments, setSegments] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    employee_count: '',
    annual_revenue: '',
    status: 'active',
    notes: '',
    key_contact_id: '',
    segments: [],
    product_lines: [],
    source: ''
  });

  useEffect(() => {
    fetchCompanies();
    fetchContacts();
    fetchSegments();
    fetchProductLines();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await contactsAPI.getAll({ status: 'active' });
      setContacts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await segmentsAPI.getAll();
      setSegments(res.data?.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments');
    }
  };

  const fetchProductLines = async () => {
    try {
      const res = await productLinesAPI.getAll();
      setProductLines(res.data || []);
    } catch (error) {
      console.error('Failed to fetch product lines');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
      annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
      key_contact_id: formData.key_contact_id || null
    };
    
    try {
      if (editingCompany) {
        await companiesAPI.update(editingCompany.id, submitData);
        toast.success('Company updated');
      } else {
        await companiesAPI.create(submitData);
        toast.success('Company created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleClickToCall = async (phoneNumber, companyName) => {
    if (!phoneNumber) {
      toast.error('No phone number available');
      return;
    }
    try {
      await voipAPI.initiateCall({
        to_number: phoneNumber,
        from_extension: 'default',
        record_call: true,
        notes: `Outbound call to company: ${companyName}`
      });
      toast.success(`Initiating call to ${phoneNumber}`);
    } catch (error) {
      // Fallback to tel: link if VoIP fails
      window.location.href = `tel:${phoneNumber}`;
      toast.info(`Opening phone dialer for ${phoneNumber}`);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      industry: company.industry || '',
      website: company.website || '',
      phone: company.phone || '',
      address: company.address || '',
      city: company.city || '',
      country: company.country || '',
      employee_count: company.employee_count?.toString() || '',
      annual_revenue: company.annual_revenue?.toString() || '',
      status: company.status,
      notes: company.notes || '',
      key_contact_id: company.key_contact_id || '',
      segments: company.segments || [],
      product_lines: company.product_lines || [],
      source: company.source || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await companiesAPI.delete(id);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const resetForm = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      industry: '',
      website: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      employee_count: '',
      annual_revenue: '',
      status: 'active',
      notes: '',
      key_contact_id: '',
      segments: [],
      product_lines: [],
      source: ''
    });
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-slate-100 text-slate-600',
    prospect: 'bg-blue-100 text-blue-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Companies</h1>
          <p className="text-muted-foreground mt-1">Manage business accounts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
              <DialogDescription>
                {editingCompany ? 'Update company information' : 'Create a new company record'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="key_contact">Key Contact</Label>
                  <Select value={formData.key_contact_id || "none"} onValueChange={(v) => handleChange('key_contact_id', v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="company-key-contact-select">
                      <SelectValue placeholder="Select key contact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {contacts.filter(c => !editingCompany || c.company_id === editingCompany.id || !c.company_id).map(contact => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name} {contact.job_title ? `(${contact.job_title})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    data-testid="company-industry-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://"
                    data-testid="company-website-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    data-testid="company-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="company-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    data-testid="company-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    data-testid="company-country-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_count">Employees</Label>
                  <Input
                    id="employee_count"
                    type="number"
                    value={formData.employee_count}
                    onChange={(e) => handleChange('employee_count', e.target.value)}
                    data-testid="company-employees-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="annual_revenue">Annual Revenue ($)</Label>
                  <Input
                    id="annual_revenue"
                    type="number"
                    value={formData.annual_revenue}
                    onChange={(e) => handleChange('annual_revenue', e.target.value)}
                    data-testid="company-revenue-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Select value={formData.source || "none"} onValueChange={(v) => handleChange('source', v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="company-source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="cold_call">Cold Call</SelectItem>
                      <SelectItem value="trade_show">Trade Show</SelectItem>
                      <SelectItem value="advertising">Advertising</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                    <SelectTrigger data-testid="company-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Product Lines */}
              <div className="space-y-2">
                <Label>Product Lines</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50">
                  {productLines.map(pl => (
                    <Badge
                      key={pl.id}
                      variant={formData.product_lines?.includes(pl.id) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.product_lines?.includes(pl.id) ? pl.color : 'bg-white hover:bg-slate-100'}`}
                      onClick={() => {
                        const current = formData.product_lines || [];
                        const updated = current.includes(pl.id)
                          ? current.filter(p => p !== pl.id)
                          : [...current, pl.id];
                        handleChange('product_lines', updated);
                      }}
                    >
                      {pl.name}
                    </Badge>
                  ))}
                  {productLines.length === 0 && (
                    <span className="text-sm text-muted-foreground">No product lines available</span>
                  )}
                </div>
              </div>
              {/* Segments */}
              <div className="space-y-2">
                <Label>Segments</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50">
                  {segments.map(seg => (
                    <Badge
                      key={seg.value}
                      variant={formData.segments?.includes(seg.value) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.segments?.includes(seg.value) ? seg.color : 'bg-white hover:bg-slate-100'}`}
                      onClick={() => {
                        const current = formData.segments || [];
                        const updated = current.includes(seg.value)
                          ? current.filter(s => s !== seg.value)
                          : [...current, seg.value];
                        handleChange('segments', updated);
                      }}
                    >
                      {seg.label}
                    </Badge>
                  ))}
                  {segments.length === 0 && (
                    <span className="text-sm text-muted-foreground">No segments available</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={2}
                  data-testid="company-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="company-submit-btn">
                  {editingCompany ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="company-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="company-filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Key Contact</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contacts</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompanies.length > 0 ? (
                filteredCompanies.map((company) => (
                  <TableRow 
                    key={company.id} 
                    className="data-table-row cursor-pointer hover:bg-slate-50" 
                    data-testid={`company-row-${company.id}`}
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-sm bg-slate-100 flex items-center justify-center">
                          <Building className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium">{company.name}</p>
                          {company.website && (
                            <a href={company.website} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              Website
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {company.key_contact ? (
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          <div>
                            <p className="font-medium text-sm">{company.key_contact.first_name} {company.key_contact.last_name}</p>
                            {company.key_contact.job_title && (
                              <p className="text-xs text-muted-foreground">{company.key_contact.job_title}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>{company.industry || '-'}</TableCell>
                    <TableCell>
                      {company.city || company.country ? (
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {[company.city, company.country].filter(Boolean).join(', ')}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{company.contact_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[company.status]}>
                        {company.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/companies/${company.id}`)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="View Company Profile"
                          data-testid={`view-company-${company.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {company.phone && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleClickToCall(company.phone, company.name)}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title={`Call ${company.phone}`}
                            data-testid={`call-company-${company.id}`}
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(company)}
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(company.id)}
                          data-testid={`delete-company-${company.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No companies found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompaniesPage;
